import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { useHistory } from 'react-router-dom'
import Modal from 'react-bootstrap/Modal'

const NOOP_GUARD = { setDirty: () => {}, registerSave: () => {}, confirmLeave: (proceed) => proceed() }

const UnsavedChangesContext = createContext(NOOP_GUARD)

/**
 * Lets the graph report unsaved mapping changes. While there are any, leaving the page (Back button,
 * browser back, closing the tab) or switching entity asks the user to save first.
 */
export const UnsavedChangesProvider = ({ children }) => {
    const history = useHistory()
    const [isDirty, setIsDirty] = useState(false)
    const [pending, setPending] = useState(null) // { proceed }
    const [isSaving, setIsSaving] = useState(false)
    const dirtyRef = useRef(false)
    const saveRef = useRef(null)

    const setDirty = useCallback((value) => {
        dirtyRef.current = value
        setIsDirty(value)
    }, [])

    /** fn() saves the open mapping and resolves true on success; null unregisters it. */
    const registerSave = useCallback((fn) => {
        saveRef.current = fn
    }, [])

    /** Runs proceed() straight away when everything is saved, otherwise after the user chooses. */
    const confirmLeave = useCallback((proceed) => {
        if (dirtyRef.current) {
            setPending({ proceed })
        } else {
            proceed()
        }
    }, [])

    // Route changes: hold the navigation and ask first
    useEffect(() => {
        if (!isDirty) {
            return undefined
        }
        const unblock = history.block((location) => {
            setPending({
                proceed: () => {
                    unblock()
                    history.push(location)
                }
            })
            return false
        })
        return unblock
    }, [isDirty, history])

    // Reload or closing the tab (the browser shows its own generic prompt)
    useEffect(() => {
        if (!isDirty) {
            return undefined
        }
        const warn = (event) => {
            event.preventDefault()
            event.returnValue = ''
        }
        window.addEventListener('beforeunload', warn)
        return () => window.removeEventListener('beforeunload', warn)
    }, [isDirty])

    const stay = () => setPending(null)

    const discard = () => {
        const { proceed } = pending
        setPending(null)
        proceed()
    }

    const saveAndLeave = async () => {
        const { proceed } = pending
        setIsSaving(true)
        let saved = false
        try {
            saved = Boolean(saveRef.current && (await saveRef.current()))
        } finally {
            setIsSaving(false)
        }
        setPending(null)
        // A failed save stays on the page: the graph shows its own error message
        if (saved) {
            proceed()
        }
    }

    const value = useMemo(() => ({ setDirty, registerSave, confirmLeave }), [setDirty, registerSave, confirmLeave])

    return (
        <UnsavedChangesContext.Provider value={value}>
            {children}
            <Modal show={Boolean(pending)} onHide={isSaving ? undefined : stay} centered backdrop="static">
                <Modal.Header closeButton={!isSaving}>
                    <Modal.Title>Unsaved changes</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    Please save your changes before you leave, or the changes will be lost.
                </Modal.Body>
                <Modal.Footer>
                    <button type="button" className="btn btn-light btn-sm" onClick={stay} disabled={isSaving}>
                        Cancel
                    </button>
                    <button type="button" className="btn btn-outline-danger btn-sm" onClick={discard} disabled={isSaving}>
                        Don&apos;t Save
                    </button>
                    <button type="button" className="btn btn-primary btn-sm" onClick={saveAndLeave} disabled={isSaving}>
                        {isSaving ? 'Saving...' : 'Save'}
                    </button>
                </Modal.Footer>
            </Modal>
        </UnsavedChangesContext.Provider>
    )
}

export const useUnsavedChanges = () => useContext(UnsavedChangesContext)
